/**
 * Tech brief · what the iPad will show (#1728; the v6 prototype's `briefUI`,
 * `.br` / `.ipad5`).
 *
 * In the side sheet between Contact and Conversation, before the visit only.
 * A dark device frame around a white "screen": the site, the contact, the
 * job's scope, the devices with this visit's action, what's on the truck, and
 * an amber "To finish you'll need" box built from the field app's real gates.
 * Whatever the office still owes shows inline in amber words. "🔒 not ready"
 * sits in the header while any before-visit preparation promise is still owed
 * (`notReadyForTech`: open, blocked, missed or waiting; booking the visit
 * itself doesn't count, since that's what dispatch does).
 *
 * The screen is drawn in fixed light colours on purpose: it's a picture of
 * the iPad, so it doesn't follow the office theme.
 */
import type { ReactNode } from "react";
import type { FinishLine } from "@/lib/finish-line";
import { notReadyForTech } from "@/lib/dispatch-preflight";
import { useTechBrief, type BriefGap, type TechBrief } from "@/lib/tech-brief";

const DEVICE_CAP = 6;

/**
 * The lock: any preparation promise before the visit still owed (the same
 * list the soft dispatch check reads, so the lock and the confirm agree).
 */
export function briefLocked(fl: FinishLine | null | undefined): boolean {
    return notReadyForTech(fl).length > 0;
}

/** "To finish you'll need: …" as one sentence, plus the filing line. */
export function finishSentence(b: TechBrief): string {
    // Lower-case each clause's first letter, but never an acronym ("IVR").
    const lower = (t: string) => (/^[A-Z]{2}/.test(t) ? t : t[0]!.toLowerCase() + t.slice(1));
    const parts = b.toFinish.map((r, i) => (i === 0 ? r.text : lower(r.text)));
    const body = parts.length > 0 ? `${parts.join("; ")}.` : "Nothing the field app enforces.";
    const filing = b.waterAuthority?.platform ? ` ${b.waterAuthority.name} files in ${b.waterAuthority.platform}.` : "";
    return `${body}${filing}`;
}

function Amber({ children, testId }: { children: ReactNode; testId?: string }) {
    return (
        <span className="font-semibold text-amber-700" data-testid={testId ?? "brief-gap"}>
            {children}
        </span>
    );
}

function Label({ children }: { children: ReactNode }) {
    return (
        <span className="block bg-white px-3.5 pt-2.5 text-[13px] font-bold uppercase tracking-wide text-stone-500">
            {children}
        </span>
    );
}

function Row({ children, className = "" }: { children: ReactNode; className?: string }) {
    return <span className={`block bg-white px-3.5 py-0.5 text-[13.5px] ${className}`}>{children}</span>;
}

export function TechBriefScreen({ brief, locked }: { brief: TechBrief; locked: boolean }) {
    const gap = (id: string): BriefGap | undefined => brief.gaps.find((g) => g.id === id);
    const devicesGap = gap("devices");
    const fieldsGap = gap("device_fields");
    const shown = brief.devices.slice(0, DEVICE_CAP);
    const more = brief.devices.length - shown.length;
    const contact = brief.contact;

    return (
        <div className="grid gap-2 border-b px-[22px] py-4" data-testid="tech-brief">
            <div className="flex items-baseline justify-between gap-2.5">
                <span className="text-[13px] text-muted-foreground">Tech brief · what the iPad will show</span>
                {locked && (
                    <span
                        className="whitespace-nowrap text-[13px] font-bold text-red-700 dark:text-red-400"
                        data-testid="tech-brief-lock"
                    >
                        🔒 not ready
                    </span>
                )}
            </div>
            <div
                className="grid rounded-2xl bg-stone-900 p-2.5 text-stone-900"
                role="group"
                aria-label="What the tech's iPad will show"
            >
                <b className="block rounded-t-[9px] bg-white px-3.5 pb-0.5 pt-3 text-[15px]">
                    {brief.site.name ?? "No site on the job"}
                </b>
                <Row className="text-[13px] text-stone-500">
                    {brief.site.address ?? "No address"}
                    {" · "}
                    {contact ? (
                        <span className="text-stone-700">
                            {[contact.name, contact.role].filter(Boolean).join(", ")}
                            {contact.phone ? ` · ${contact.phone}` : ""}
                        </span>
                    ) : (
                        <Amber>{gap("contact")?.words ?? "On-site contact not set"}</Amber>
                    )}
                </Row>
                {(brief.site.accessNotes || brief.site.gateCodeOnFile) && (
                    <Row className="text-[13px] text-stone-600">
                        {brief.site.accessNotes}
                        {brief.site.accessNotes && brief.site.gateCodeOnFile ? " · " : ""}
                        {brief.site.gateCodeOnFile ? "Gate code on file" : ""}
                    </Row>
                )}
                {gap("appointment") && (
                    <Row>
                        <Amber testId="brief-gap-appointment">{gap("appointment")!.words}</Amber>
                    </Row>
                )}

                <Label>The job</Label>
                <Row>
                    {brief.scope.text ?? brief.scope.techNotes ?? <Amber>{gap("scope")?.words}</Amber>}
                    {(brief.scope.text || brief.scope.techNotes) && gap("scope") && (
                        <>
                            {" "}
                            <Amber>· {gap("scope")!.words}</Amber>
                        </>
                    )}
                </Row>
                {brief.scope.text && brief.scope.techNotes && (
                    <Row className="text-stone-600">{brief.scope.techNotes}</Row>
                )}
                {brief.checkIn && (
                    <Row className="text-stone-600">
                        IVR check-in{brief.checkIn.phone ? ` · ${brief.checkIn.phone}` : ""}
                        {brief.checkIn.pin ? ` · PIN ${brief.checkIn.pin}` : ""}
                    </Row>
                )}

                <Label>Devices</Label>
                {shown.length > 0 ? (
                    <ol
                        className="m-0 list-decimal bg-white py-0.5 pl-8 pr-3.5 text-[13.5px]"
                        data-testid="brief-devices"
                    >
                        {shown.map((d) => (
                            <li key={d.assetId}>
                                {d.label} · <b className="font-semibold">{d.action}</b>
                                {d.location ? <span className="text-stone-500"> · {d.location}</span> : null}
                                {d.missing.length > 0 && (
                                    <>
                                        {" "}
                                        <Amber>· missing {d.missing.map((m) => m.toLowerCase()).join(", ")}</Amber>
                                    </>
                                )}
                            </li>
                        ))}
                    </ol>
                ) : null}
                {more > 0 && <Row className="text-stone-500">and {more} more</Row>}
                {devicesGap && (
                    <Row>
                        <Amber>{devicesGap.words}</Amber>
                    </Row>
                )}
                {fieldsGap && shown.length > 0 && (
                    <Row>
                        <Amber testId="brief-gap-fields">{fieldsGap.words}</Amber>
                    </Row>
                )}

                <Label>On the truck</Label>
                {brief.parts.length > 0 ? (
                    brief.parts.map((p, i) => (
                        <Row key={`${p.name}-${i}`}>
                            {p.quantity > 1 ? `${p.quantity} × ` : ""}
                            {p.name} ·{" "}
                            {p.state === "staged" || p.state === "with_tech" || p.state === "received" ? (
                                <span className="font-semibold text-emerald-700">{p.words}</span>
                            ) : (
                                <Amber>{p.words}</Amber>
                            )}
                        </Row>
                    ))
                ) : (
                    <Row className="text-stone-600">No parts ordered for this job</Row>
                )}

                <span className="block rounded-b-[9px] bg-white pt-2">
                    <span
                        className="block rounded-b-[9px] bg-amber-100 px-3.5 pb-3 pt-2.5 text-[13.5px] text-amber-950"
                        data-testid="brief-must"
                    >
                        <b className="font-semibold">To finish you'll need:</b> {finishSentence(brief)}
                    </span>
                </span>
            </div>
        </div>
    );
}

/** The block: reads the brief, shows nothing after the visit. */
export function TechBriefBlock({
    jobId,
    finishLine,
    preVisit,
}: {
    jobId: string;
    finishLine: FinishLine | null;
    preVisit: boolean;
}) {
    const q = useTechBrief(jobId, preVisit);
    if (!preVisit) return null;
    if (q.isLoading) {
        return (
            <p
                className="m-0 border-b px-[22px] py-4 text-[13.5px] text-muted-foreground"
                data-testid="tech-brief-loading"
            >
                Reading what the iPad will show…
            </p>
        );
    }
    if (!q.data) {
        return (
            <p
                className="m-0 border-b px-[22px] py-4 text-[13.5px] text-muted-foreground"
                data-testid="tech-brief-error"
            >
                Couldn't load the tech brief.
            </p>
        );
    }
    return <TechBriefScreen brief={q.data} locked={briefLocked(finishLine)} />;
}
